import { Big } from 'big.js';
import type { Order, Position } from '@/api/spec';
import { Side } from '@/common/constants';
import type { MarketData } from '@/common/MarketData';
import { amountToBig } from '@/utils/bigUtils';
import { calculateLiquidationPrice } from '@/utils/liquidationUtils';
import { calculateUnrealizedPnl, effectiveEntryPriceForPnl } from '@/utils/pnlUtils';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils';
import { extractLinkedTpSl } from './extractLinkedTpSl';
import { toBasePosition } from './positionUtils';
import { mapApiPositionStatus, type ViewPosition } from './types';

export const mapPositionsToViewModel = (
  apiPositions: Position[],
  markets: ReadonlyMap<number, MarketData>,
  orders?: Order[]
): ViewPosition[] => {
  const viewPositions: ViewPosition[] = [];
  const ordersByLp = new Map<number, Order[]>();

  if (orders) {
    for (const order of orders) {
      if (order.lp != null) {
        const lp = Number(order.lp);
        const existing = ordersByLp.get(lp);
        if (existing) {
          existing.push(order);
        } else {
          ordersByLp.set(lp, [order]);
        }
      }
    }
  }

  for (const apiPosition of apiPositions) {
    const market = markets.get(apiPosition.mkt);

    if (market) {
      const base = toBasePosition(apiPosition, market);
      if (!base) continue;

      const marketPrice = market.markPrice;

      const { takeProfit, stopLoss, takeProfitLimitPrice, stopLossLimitPrice, tpSlPercentage } = orders
        ? extractLinkedTpSl(
            ordersByLp.get(base.positionId) || [],
            base.side,
            market.quoteDecimals,
            base.assetDecimals,
            base.size
          )
        : {};

      const value = new Big(base.size).times(marketPrice);

      // Lift the stored (asymmetrically-rounded) entry price by its Q16 residue so unrealized
      // delta PnL matches the protocol's canonical entry price. Display keeps the stored value.
      const pnlEntryPrice = effectiveEntryPriceForPnl({
        side: base.side,
        entryPrice: base.entryPrice,
        entryPriceResidue: apiPosition.epr,
        priceDecimals: base.quoteDecimals,
      });

      const { pricePnl, premiumPnl: funding } = calculateUnrealizedPnl({
        side: base.side,
        size: base.size,
        entryPrice: pnlEntryPrice,
        markPrice: marketPrice,
        entryFundingSum: offsetNumberToNumber(apiPosition.efs, base.quoteDecimals),
        currentFundingSum: market.fundingSum ?? 0,
        fundingSumDivider: market.fundingSumDivider,
      });

      // Positions table shows unrealized PNL (price move + funding) and intentionally excludes fees,
      // which settle on close — fee-inclusive PNL lives in the history tables.
      const unrealizedPnl = pricePnl.plus(funding);
      const roe = base.collateral && base.collateral.gt(0) ? unrealizedPnl.div(base.collateral) : new Big(0);
      const realizedPnl = apiPosition.dpnl ? amountToBig(apiPosition.dpnl, market.quote.decimals).toNumber() : undefined;

      // Valid only here: snapshots report `c` as the standing collateral. History events report
      // it as a signed change, which is why PNLEntry carries no derived leverage at all.
      const leverage = base.collateral.gt(0) ? Math.round(base.positionValueAtEntry.div(base.collateral).toNumber()) : 1;

      const liquidationPrice = calculateLiquidationPrice({
        entryPrice: base.entryPrice,
        collateral: base.collateral,
        size: base.size,
        side: base.side,
        maintenanceMarginFraction: market.maintenanceMarginFraction,
        funding,
      });

      viewPositions.push({
        positionId: base.positionId,
        uniqueId: base.positionId.toString(),
        txHash: base.txHash,
        marketId: base.marketId,
        openTimestamp: base.openTimestamp,
        status: mapApiPositionStatus(apiPosition.st),
        statusReason: apiPosition.sr,
        side: base.side,
        assetCurrency: base.assetCurrency,
        assetDecimals: base.assetDecimals,
        quoteDecimals: base.quoteDecimals,
        leverage,
        initialLeverage: base.initialLeverage,
        size: base.size,
        value: value.toNumber(),
        entryPrice: base.entryPrice,
        markPrice: marketPrice,
        pnl: unrealizedPnl.toNumber(),
        realizedPnl,
        fee: base.fee.toNumber(),
        roe: roe.toNumber(),
        liquidationPrice: liquidationPrice,
        margin: base.collateral.toNumber(),
        marginType: 'isolated', // TODO
        funding: funding.toNumber(),
        takeProfit,
        stopLoss,
        takeProfitLimitPrice,
        stopLossLimitPrice,
        tpSlPercentage,
        isBuy: base.side === Side.long,
        boost: market.boost,
      });
    }
  }

  return viewPositions;
};
